/* =========================================================
   VRO weather + location rail
   Rewrites a section's heading/subheading with the shopper's city and the
   weather there, and — when the section asks for it — shows only the
   products tagged for that weather.

   Deliberate constraints:

   - Inside the section: no markup and no styling. It replaces text inside
     elements the section already renders and hides cards it already
     rendered, so the layout is the theme's own either way. The only thing
     it draws is the consent card described below, which sits over the page
     and touches nothing in the section.
   - The server already rendered today's exact copy and the usual products.
     This only overwrites once real values are in hand, so a failed request,
     a blocked network or no JS at all leaves the section as it was.
   - Filtering never empties the rail. If nothing matches today's weather
     the usual products stay — and without location permission nothing is
     filtered at all, since the weather would only be the fallback city's.

   Location: on a first-time visitor the section can ask once (the
   "weather_ask" setting). It goes straight to the browser's own permission
   dialog — no card in the way, nothing to click through first.

   A browser is allowed to answer a request made on page load with a quiet
   chip in the address bar instead of that dialog, and then the shopper
   sees nothing at all. So the request is watched for a few seconds
   afterwards, and only if nothing was ever put in front of the shopper
   does a small card of ours appear offering the same thing from a button —
   a request behind a click always gets the dialog. Whichever way it is
   answered, the answer is remembered and the shopper is not asked again on
   that browser.

   Nothing is asked of someone who already granted the permission (they are
   simply detected) or already refused it at the browser level. Detection
   itself is the theme's existing one (window.VroLocation, assets/custom.js):
   same permission check, same reverse geocode, same cache — so the header's
   city and this section always agree.

   Weather: Open-Meteo. No API key and no account, so there is nothing to
   configure and no key to leak in client-side code.
   ========================================================= */
(function () {
  'use strict';

  /* Two sections load this file — feed-rail for the rail, header for the
     hover card — and a page carrying both would otherwise run all of it
     twice: two sets of listeners, two location prompts. The browser serves
     the second tag from cache, so the only cost is this check. */
  if (window.VroWeatherLoaded) return;
  window.VroWeatherLoaded = true;

  var LOCATION_CACHE_KEY = 'vro_detected_location';
  /* v2: the first version asked the browser directly at load and marked
     everyone it ran for as asked, including the shoppers Chrome never
     showed a dialog to. They get the card. */
  var ASKED_KEY = 'vro_location_asked_v2';
  var WEATHER_CACHE_KEY = 'vro_weather_v3';
  var WEATHER_CACHE_MS = 30 * 60 * 1000;
  var ENDPOINT = 'https://api.open-meteo.com/v1/forecast';

  var HOT_ABOVE = 32;
  var COLD_BELOW = 16;

  /* WMO weather codes -> the words we print and the bucket we filter on.
     https://open-meteo.com/en/docs — grouped, because "light drizzle" and
     "dense drizzle" are the same sentence to a shopper. */
  /* `phrase` is written to sit after "for ...", e.g. "for rainy days". */
  /* `wear` is the same reading as `word`, but shaped to sit in front of a
     noun: "raining" is what the sky is doing and reads right in a sentence,
     "Rainy" is what the clothes are for and reads right on a card that says
     "View All Rainy Wear". Capitalised here because that is the only place
     it is used. */
  function describe(code) {
    if (code === 0) return { word: 'clear', wear: 'Clear-Sky', phrase: 'clear skies', bucket: 'clear' };
    if (code <= 2) return { word: 'sunny', wear: 'Sunny', phrase: 'sunny days', bucket: 'clear' };
    if (code === 3) return { word: 'cloudy', wear: 'Cloudy', phrase: 'grey skies', bucket: 'cloud' };
    if (code >= 45 && code <= 48) return { word: 'foggy', wear: 'Foggy', phrase: 'foggy days', bucket: 'cloud' };
    if (code >= 51 && code <= 57) return { word: 'drizzly', wear: 'Drizzle', phrase: 'drizzly days', bucket: 'rain' };
    if (code >= 61 && code <= 67) return { word: 'raining', wear: 'Rainy', phrase: 'rainy days', bucket: 'rain' };
    if (code >= 71 && code <= 77) return { word: 'snowing', wear: 'Snow', phrase: 'the cold', bucket: 'cold' };
    if (code >= 80 && code <= 82) return { word: 'raining', wear: 'Rainy', phrase: 'rainy days', bucket: 'rain' };
    if (code >= 85 && code <= 86) return { word: 'snowing', wear: 'Snow', phrase: 'the cold', bucket: 'cold' };
    if (code >= 95) return { word: 'stormy', wear: 'Storm', phrase: 'stormy weather', bucket: 'rain' };
    return { word: 'mild', wear: 'Everyday', phrase: 'days like today', bucket: 'clear' };
  }

  /* The words for a shelf chosen by temperature rather than by the sky. */
  var BUCKET_WORDS = {
    cold: { word: 'cold', wear: 'Winter', phrase: 'the cold' },
    hot: { word: 'hot', wear: 'Summer', phrase: 'hot days' }
  };

  /* Rain wins over temperature — a hot rainy day still calls for rain gear.
     Otherwise the extremes override a merely clear sky.

     Cold is judged on the day's low as well as the reading now: a hill
     town at 20° in the afternoon is 8° by evening, and a shopper there
     wants the winter shelf, not the clear-sky one. The two lines come
     from the rail's own settings (data-vro-weather-cold-below / -hot-above)
     and fall back to the theme's numbers. */
  function thresholdsOf(section) {
    var cold = parseFloat(section && section.getAttribute('data-vro-weather-cold-below'));
    var hot = parseFloat(section && section.getAttribute('data-vro-weather-hot-above'));
    return {
      cold: isFinite(cold) ? cold : COLD_BELOW,
      hot: isFinite(hot) ? hot : HOT_ABOVE
    };
  }

  function bucketFor(described, temp, weather, limits) {
    limits = limits || { cold: COLD_BELOW, hot: HOT_ABOVE };
    var low = weather && typeof weather.min === 'number' ? weather.min : null;
    if (described.bucket === 'rain') return 'rain';
    if (typeof temp === 'number' && temp >= limits.hot) return 'hot';
    if (typeof temp === 'number' && temp <= limits.cold) return 'cold';
    if (low !== null && low <= limits.cold) return 'cold';
    return described.bucket;
  }

  /* Polls for something another deferred script defines, then calls back.
     Gives up after ~3s and calls back with false rather than hanging. */
  function waitFor(test, done) {
    if (test()) { done(true); return; }
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (test()) { clearInterval(timer); done(true); return; }
      if (tries > 30) { clearInterval(timer); done(false); }
    }, 100);
  }

  function readJSON(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }

  function detectedLocation() {
    var cached = readJSON(LOCATION_CACHE_KEY);
    if (!cached || !cached.label) return null;
    /* No expiry, matching custom.js, which owns this cache: the pill and
       this card read the same entry, so the moment one of them expires it
       and the other does not the header says one city and the card another
       — which is exactly how "Mumbai" ended up under the logo with "Jaipur"
       in the card hanging off it. A stale city is re-detected in custom.js,
       not swapped for the merchant's default. */

    /* The header's label carries the state as well — "Jaipur, Rajasthan" —
       because that is what names a place to a shopper. A heading and a
       weather card want the city alone: "Made for Jaipur", not "Made for
       Jaipur, Rajasthan". custom.js caches both; entries written before it
       did, and any it could only name in one piece, still give us the one.

       Entries written before lat/lon were cached still give us the city. */
    return { label: cached.city || cached.label, lat: cached.lat, lon: cached.lon };
  }

  function fetchWeather(lat, lon) {
    var cached = readJSON(WEATHER_CACHE_KEY);
    if (cached && cached.savedAt && (Date.now() - cached.savedAt) < WEATHER_CACHE_MS
        && cached.lat === lat && cached.lon === lon) {
      return Promise.resolve(cached);
    }

    /* temperature and the code drive the rail; the other three are for the
       header's hover card. Chance of rain is an hourly variable — there is
       no "current" one — so today's hours come along and the one matching
       now is picked out below. */
    var url = ENDPOINT + '?latitude=' + encodeURIComponent(lat)
            + '&longitude=' + encodeURIComponent(lon)
            + '&current=temperature_2m,weather_code,apparent_temperature,relative_humidity_2m'
            + '&hourly=precipitation_probability&daily=temperature_2m_min,temperature_2m_max&forecast_days=1&timezone=auto';

    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('weather request failed');
        return res.json();
      })
      .then(function (data) {
        var current = data && data.current;
        if (!current || typeof current.temperature_2m !== 'number') {
          throw new Error('weather response had no reading');
        }
        var result = {
          temp: Math.round(current.temperature_2m),
          code: current.weather_code,
          feels: typeof current.apparent_temperature === 'number' ? Math.round(current.apparent_temperature) : null,
          humidity: typeof current.relative_humidity_2m === 'number' ? Math.round(current.relative_humidity_2m) : null,
          /* Today's low and high, for the cold rule above. */
          min: dailyReading(data, 'temperature_2m_min'),
          max: dailyReading(data, 'temperature_2m_max'),
          rain: rainChanceNow(data, current.time),
          lat: lat, lon: lon,
          savedAt: Date.now()
        };
        writeJSON(WEATHER_CACHE_KEY, result);
        return result;
      });
  }

  function dailyReading(data, key) {
    var daily = data && data.daily;
    var list = daily && daily[key];
    return (list && typeof list[0] === 'number') ? Math.round(list[0]) : null;
  }

  /* Open-Meteo returns the hours of the day and a timestamp for "now", both
     in the same local time. Match them on the hour; anything unexpected in
     the response is worth no guess at all. */
  function rainChanceNow(data, nowStamp) {
    var hourly = data && data.hourly;
    if (!hourly || !hourly.time || !hourly.precipitation_probability) return null;
    var hour = String(nowStamp || '').slice(0, 13);
    for (var i = 0; i < hourly.time.length; i++) {
      if (String(hourly.time[i]).slice(0, 13) === hour) {
        var value = hourly.precipitation_probability[i];
        return typeof value === 'number' ? value : null;
      }
    }
    return null;
  }

  /* {city} {temp} {weather} {conditions} {wear} — anything unresolved leaves
     the template alone rather than printing a stray brace.

     A token written with a capital — {Weather} — asks for the value with a
     capital, so the same template can open a sentence or sit mid-sentence
     without a second token for each. {wear} already arrives capitalised
     because there is nowhere it belongs but the head of a label. */
  var TOKEN = /\{(city|temp|weather|conditions|wear)\}/gi;

  function fill(template, values) {
    return String(template).replace(TOKEN, function (match, key) {
      var value = values[key.toLowerCase()];
      if (value === undefined || value === null) return match;
      value = String(value);
      var head = key.charAt(0);
      if (head === head.toUpperCase() && head !== head.toLowerCase()) {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return value;
    });
  }

  function applyText(section, values) {
    section.querySelectorAll('[data-vro-weather-text]').forEach(function (el) {
      var template = el.getAttribute('data-vro-weather-text');
      if (!template) return;
      /* Read the server's own copy before overwriting it, so restoreText
         below has something to put back when the shopper switches off. */
      original(el);
      var next = fill(template, values);
      /* Still holding a token means we lack something it asked for — leave
         the server-rendered copy alone. */
      TOKEN.lastIndex = 0;
      if (TOKEN.test(next)) return;
      TOKEN.lastIndex = 0;
      el.textContent = next;
    });
  }

  /* The other half of applyText: with the weather picks switched off, a
     label reading "Sunny Wear" is claiming a reading nothing is being
     filtered by. Put the merchant's own words back. */
  function restoreText(section) {
    section.querySelectorAll('[data-vro-weather-text]').forEach(function (el) {
      write(el, original(el));
    });
  }

  var BUCKET_KEYS = ['rain', 'hot', 'cold', 'clear', 'cloud'];

  /* The five tags this section treats as weather tags. A product carrying
     none of them is one of the rail's usual products — a plain shirt rather
     than a fleece — which is what the rail falls back to and what tops up a
     thin weather row. Derived from the section's own attributes, so the
     merchant's tag names are the only source of truth. */
  /* A bucket may name more than one tag, comma separated.

     One word per bucket was the original shape and it does not survive
     contact with a real catalogue: wet weather is tagged "Monsoon" on the
     jeans, "Rain Jacket" on the rainsuits, and a merchant who picks one
     loses the other half of the shelf. The Liquid that marks a card
     seasonal already splits these on commas; this is the matching half of
     the same rule, so both ends agree on what counts as a weather tag. */
  function bucketTags(section, bucket) {
    return (section.getAttribute('data-vro-weather-tag-' + bucket) || '')
      .split(',')
      .map(function (tag) { return tag.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function seasonTagSet(section) {
    var set = [];
    BUCKET_KEYS.forEach(function (key) {
      bucketTags(section, key).forEach(function (tag) {
        if (set.indexOf(tag) === -1) set.push(tag);
      });
    });
    return set;
  }

  function cardTags(card) {
    return (card.getAttribute('data-vro-season') || '').split(',').map(function (tag) {
      return tag.trim().toLowerCase();
    }).filter(Boolean);
  }

  function isSeasonal(card, set) {
    return cardTags(card).some(function (tag) { return set.indexOf(tag) !== -1; });
  }

  function railCards(section) {
    return Array.prototype.slice.call(section.querySelectorAll('.feed-card[data-vro-season]'));
  }

  /* The weather a card was drawn for, when it came off a shelf the merchant
     nominated for that weather rather than out of the rail's own
     collection. Empty for every ordinary card. */
  function cardBucket(card) {
    return (card.getAttribute('data-vro-wx-bucket') || '').trim().toLowerCase();
  }

  /* Order matters: the collection's own, which is the order the rail was
     rendered in. Everything below picks a subset and never reorders, so a
     merchant's merchandising survives every mode. */
  function inOrder(cards, wanted) {
    return cards.filter(function (card) { return wanted.indexOf(card) !== -1; });
  }

  function showOnly(cards, chosen) {
    cards.forEach(function (card) { card.hidden = true; });
    chosen.forEach(function (card) { card.hidden = false; });
  }

  /* Everything the rail holds, in collection order: the whole category
     mixed together, shirts beside jackets. This is the resting state and
     what the server already rendered, so it is also what putting the rail
     back looks like. */
  function showMixed(section) {
    var cards = railCards(section);
    if (!cards.length) return 0;
    /* The rail's own collection, and only that. Cards drawn from a
       nominated weather shelf are not part of this category as the merchant
       arranged it — they were fetched to answer a particular sky — so with
       the picks off they stay where they were, out of sight. */
    var own = cards.filter(function (card) { return cardBucket(card) === ''; });
    showOnly(cards, own.length ? own : cards);
    return own.length ? own.length : cards.length;
  }

  /* opts.strict — we know where the shopper is, so the weather is theirs and
     the rail is only what suits it.
     opts.strict === false — the weather belongs to the merchant's fallback
     city, which is a guess about the shopper. Lead with what suits it, then
     top up with the rail's usual products, so the row is useful without
     pretending to know something it does not. */
  function applyFilter(section, bucket, opts) {
    if (section.getAttribute('data-vro-weather-filter') !== '1') return 0;

    var cards = railCards(section);
    if (!cards.length) return 0;

    var strict = !opts || opts.strict !== false;
    var wanted = bucketTags(section, bucket);
    var set = seasonTagSet(section);

    var show = parseInt(section.getAttribute('data-vro-weather-show'), 10);
    if (!isFinite(show) || show < 1) show = cards.length;

    /* A card off the weather's own shelf counts first and counts whole: the
       merchant put it there for exactly this weather, which is a firmer
       statement than a tag someone may or may not have added. Then the
       tagged ones, which is how a rail with no shelves nominated still
       works and how the rail's own collection still contributes. Order is
       kept within each group, so merchandising survives. */
    var shelved = cards.filter(function (card) { return cardBucket(card) === bucket; });
    var tagged = wanted.length ? cards.filter(function (card) {
      return cardBucket(card) === '' &&
        cardTags(card).some(function (tag) { return wanted.indexOf(tag) !== -1; });
    }) : [];
    var matching = shelved.concat(tagged);

    /* The rail's usual products: what is left when everything spoken for by
       some weather is set aside. Computed from the tags and the shelves
       rather than from what happened to be visible, because with the switch
       on the page every card is rendered visible — off is the resting
       state. A card from another weather's shelf is not a usual product; it
       is winter stock waiting for winter, and topping a sunny row up with
       it would be worse than a short row. */
    var usual = cards.filter(function (card) {
      return cardBucket(card) === '' && !isSeasonal(card, set);
    });

    var chosen;
    if (!matching.length) {
      /* No tag configured for today, or nothing carries it: the usual
         products rather than an empty rail — or every season at once. */
      chosen = usual.length ? usual.slice(0, show) : cards.slice(0, show);
    } else if (strict) {
      chosen = matching.slice(0, show);
    } else {
      var picked = matching.slice(0, show);
      for (var i = 0; i < usual.length && picked.length < show; i++) {
        if (picked.indexOf(usual[i]) === -1) picked.push(usual[i]);
      }
      chosen = inOrder(cards, picked);
    }

    if (!chosen.length) return 0;
    showOnly(cards, chosen);
    return chosen.length;
  }

  /* ------------------------------------------------------------------ *
     The switch.

     Three things this rail can be, and the switch picks between the first
     and the other two:

       off                  the whole category, mixed, in its own order
       on  + location       only what suits the shopper's weather
       on  without location the fallback city's weather first, then the
                            rail's usual products behind it

     Off is the resting position and the one the server renders, so the
     switch never needs to fetch anything: every card is already on the
     page and switching only changes which are hidden. No request, no
     reload, nothing to fail halfway.
   * ------------------------------------------------------------------ */

  var SWITCH_KEY_PREFIX = 'vro_weather_picks:';

  function switchKey(section) {
    return SWITCH_KEY_PREFIX + (section.getAttribute('data-vro-weather-toggle') || '');
  }

  /* A switch this width does not draw. The merchant can keep the switch
     off phones (feed-rail's "Show the switch on phones"), and a switch
     nobody can reach must not leave the rail parked in the off position
     with no way to the picks — so it counts the same as no switch at all.
     Computed display, since the hidden mount is still in the DOM. */
  function switchHidden(section) {
    var input = section.querySelector('[data-vro-weather-switch]');
    if (!input) return false;
    var mount = input.closest('.vro-wx-toggle-mount') || input;
    try { return window.getComputedStyle(mount).display === 'none'; } catch (e) { return false; }
  }

  /* Off unless this shopper turned it on before, on this browser. A first
     visit always shows the whole category. */
  function switchedOn(section) {
    if (!section.hasAttribute('data-vro-weather-toggle')) return true;
    if (switchHidden(section)) return true;
    try { return localStorage.getItem(switchKey(section)) === '1'; } catch (e) { return false; }
  }

  function rememberSwitch(section, on) {
    try { localStorage.setItem(switchKey(section), on ? '1' : '0'); } catch (e) { /* private mode */ }
  }

  /* The copy the server rendered, kept so the off state can put it back.
     Read before anything overwrites it. */
  function original(el) {
    if (el && !el.hasAttribute('data-vro-weather-orig')) {
      el.setAttribute('data-vro-weather-orig', el.textContent);
    }
    return el ? el.getAttribute('data-vro-weather-orig') : '';
  }

  function headingsOf(section) {
    return {
      heading: section.querySelector('.feed-heading'),
      sub: section.querySelector('.rail-subheading')
    };
  }

  function write(el, text) {
    if (!el || text === null || text === undefined || text === '') return;
    if (el.textContent === text) return;
    el.textContent = text;
  }

  /* Off: say what the rail is, not where the shopper is. A heading naming a
     city would be claiming these products were chosen for it, and in this
     state they were not. */
  function applyOffText(section) {
    var els = headingsOf(section);
    original(els.heading);
    original(els.sub);
    write(els.heading, section.getAttribute('data-vro-weather-off-heading') || original(els.heading));
    write(els.sub, section.getAttribute('data-vro-weather-off-subheading') || original(els.sub));
  }

  function note(section, text) {
    var el = section.querySelector('[data-vro-weather-note]');
    if (el) el.textContent = text || '';
  }

  /* ---------------------------------------------------------------
     The View All tile, where the section did not render it itself

     sections/feed-rail.liquid draws the six glyphs and gives the label its
     template, and that is the better place for both: nothing is injected,
     nothing flashes, and the tile is right before this file runs. This is
     the same work done from here, and it runs only where that markup is
     absent — a theme whose section file is older than this one. Once the
     section catches up, every branch below finds what it was going to
     build already there and leaves it alone.

     The glyphs are the same paths, so the two cannot drift into two
     different suns: change one, change both.
  --------------------------------------------------------------- */
  var GLYPH_PATHS = {
    clear: '<circle cx="12" cy="12" r="4.2"/><path d="M12 2.4v2.3M12 19.3v2.3M4.2 12H1.9M22.1 12h-2.3'
         + 'M6.5 6.5 4.9 4.9M19.1 19.1l-1.6-1.6M17.5 6.5l1.6-1.6M4.9 19.1l1.6-1.6"/>',
    hot:   '<circle cx="12" cy="9.5" r="3.6"/><path d="M12 1.9v1.9M12 15.2v1.2M4.9 9.5H3.4M20.6 9.5h-1.5'
         + 'M6.9 4.4 5.8 3.3M18.2 3.3l-1.1 1.1"/><path d="M3.6 19.1c1.4-1.2 2.9-1.2 4.3 0s2.9 1.2 4.3 0'
         + ' 2.9-1.2 4.3 0 2.9 1.2 4.3 0"/>',
    cloud: '<path d="M7.4 18.4h9.9a4 4 0 0 0 .5-8 5.7 5.7 0 0 0-10.9 1.3 3.4 3.4 0 0 0 .5 6.7Z"/>',
    rain:  '<path d="M7.6 15.4h9.4a3.8 3.8 0 0 0 .5-7.6A5.5 5.5 0 0 0 7 9.1a3.2 3.2 0 0 0 .6 6.3Z"/>'
         + '<path d="M9 18.3l-.9 2.4M13 18.3l-.9 2.4M17 18.3l-.9 2.4"/>',
    cold:  '<path d="M12 2.6v18.8M3.9 7.3l16.2 9.4M20.1 7.3 3.9 16.7"/><path d="M9.7 4.5 12 6.8l2.3-2.3'
         + 'M9.7 19.5 12 17.2l2.3 2.3M4.2 11.1l.8 3.1-3 .9M19.8 12.9l-.8-3.1 3-.9M4.2 12.9l.8-3.1-3-.9'
         + 'M19.8 11.1l-.8 3.1 3 .9"/>'
  };

  function glyphSvg(bucket) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'feed-view-all-glyph');
    svg.setAttribute('data-vro-weather-glyph', bucket);
    svg.setAttribute('width', '26');
    svg.setAttribute('height', '26');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = GLYPH_PATHS[bucket] || '';
    return svg;
  }

  var LABEL_TEMPLATE = '{wear} Wear';

  function upgradeViewAll(section) {
    var tile = section.querySelector('.feed-view-all');
    if (!tile || tile.__vroWeatherTile) return;
    tile.__vroWeatherTile = true;

    var icon = tile.querySelector('.feed-view-all-icon');
    if (icon && !icon.querySelector('[data-vro-weather-glyph]')) {
      /* The umbrella the section drew becomes the resting glyph, so a rail
         with no reading looks exactly as it did. */
      var resting = icon.querySelector('svg');
      if (resting) {
        resting.classList.add('feed-view-all-glyph');
        resting.setAttribute('data-vro-weather-glyph', 'default');
      }
      tile.classList.add('feed-view-all--weather');
      BUCKET_KEYS.forEach(function (bucket) {
        icon.insertBefore(glyphSvg(bucket), icon.firstChild);
      });
    }

    var category = tile.querySelector('.feed-view-all-category');
    if (category && !category.querySelector('[data-vro-weather-text]')) {
      /* Only the words move into the span — the chevron beside them is the
         tile's, not the label's, and writing over it would take it away. */
      var words = document.createElement('span');
      words.className = 'feed-view-all-category-text';
      words.setAttribute('data-vro-weather-text', LABEL_TEMPLATE);
      var node = category.firstChild;
      while (node) {
        var next = node.nextSibling;
        if (node.nodeType === 3) {
          var text = node.textContent.trim();
          if (text) words.textContent = text;
          category.removeChild(node);
        }
        node = next;
      }
      if (words.textContent) category.insertBefore(words, category.firstChild);
    }
  }

  /* Paints whichever of the three states applies, from whatever is known
     right now. Safe to call before the weather has arrived: the off state
     needs nothing, and the on state repaints itself when it lands. */
  /* The View All tile opens the shelf for the weather it is naming, where
     the rail has one set for it; otherwise, and with the picks off, the
     link the server drew. */
  function pointViewAll(section, bucket) {
    var tile = section.querySelector('.feed-view-all');
    if (!tile) return;
    if (!tile.hasAttribute('data-vro-weather-url-default')) {
      tile.setAttribute('data-vro-weather-url-default', tile.getAttribute('href') || '');
    }
    var url = bucket ? tile.getAttribute('data-vro-weather-url-' + bucket) : '';
    tile.setAttribute('href', url || tile.getAttribute('data-vro-weather-url-default'));
  }

  function paint(section) {
    var els = headingsOf(section);
    original(els.heading);
    original(els.sub);

    if (!switchedOn(section)) {
      showMixed(section);
      /* Server copy back first, then the off-state copy over the top of it:
         the heading and subheading are governed by both — a live template
         while the picks are on, their own off- attributes while they are
         off — and it is the off copy that has to win here. */
      restoreText(section);
      applyOffText(section);
      note(section, '');
      section.setAttribute('data-vro-weather-mode', 'mixed');
      /* Drives the View All tile's icon back to its neutral glyph: with no
         reading in play there is no weather for it to name. */
      section.removeAttribute('data-vro-weather-bucket');
      pointViewAll(section, '');
      return;
    }

    /* Back to the merchant's own copy first. Where a live template exists
       applyText overwrites it a line later; where one does not, this is
       what undoes the off-state copy — otherwise a rail whose live heading
       is blank would keep saying "Everything in this edit" while showing
       only the weather picks. */
    write(els.heading, original(els.heading));
    write(els.sub, original(els.sub));

    /* A rail with a hand-picked line-up has already answered the
       no-location case. The merchant named those products for the shopper
       we know nothing about, and a fallback city's forecast — which is all
       we have until a real position turns up — has nothing to add to that.
       The server rendered the picks visible with the collection hidden
       behind them, so the right move is to leave the cards exactly as they
       came and keep the merchant's own copy over them. The moment a
       position is granted this falls through and the weather takes the
       rail over exactly as it used to. */
    if (section.getAttribute('data-vro-weather-picked') === '1' && !haveDetectedPosition()) {
      section.setAttribute('data-vro-weather-mode', 'picked');
      section.removeAttribute('data-vro-weather-bucket');
      pointViewAll(section, '');
      note(section, '');
      return;
    }

    var known = section.__vroWeather;
    if (!known) {
      /* Asked for weather picks before the weather is in hand. Leave the
         cards alone rather than guessing — this repaints the moment the
         request resolves. */
      section.setAttribute('data-vro-weather-mode', 'pending');
      section.removeAttribute('data-vro-weather-bucket');
      pointViewAll(section, '');
      note(section, '');
      return;
    }

    applyText(section, known.values);
    /* A subheading written for this weather in particular wins over the
       live template: "Layer up, {city} is at {temp}" for cold, another
       line for rain. Same tokens, same rule about an unfilled one. */
    var perWeather = section.getAttribute('data-vro-weather-sub-' + known.bucket);
    if (perWeather && els.sub) {
      var line = fill(perWeather, known.values);
      TOKEN.lastIndex = 0;
      if (!TOKEN.test(line)) write(els.sub, line);
      TOKEN.lastIndex = 0;
    }
    /* The one attribute the View All tile's icon reads. Set after the copy
       so the glyph and the words change on the same frame. */
    section.setAttribute('data-vro-weather-bucket', known.bucket);
    pointViewAll(section, known.bucket);

    var strict = haveDetectedPosition();
    applyFilter(section, known.bucket, { strict: strict });
    section.setAttribute('data-vro-weather-mode', strict ? 'weather' : 'weather-approx');
    note(section, strict ? '' :
      'Weather for ' + known.values.city + '. Allow location for picks that match yours.');
  }

  function render(section) {
    var defaultCity = section.getAttribute('data-vro-weather-city') || '';
    var lat = parseFloat(section.getAttribute('data-vro-weather-lat'));
    var lon = parseFloat(section.getAttribute('data-vro-weather-lon'));

    var located = detectedLocation();
    var city = (located && located.label) || defaultCity;
    if (located && typeof located.lat === 'number' && typeof located.lon === 'number') {
      lat = located.lat;
      lon = located.lon;
    }

    /* Where the section file is older than this one, the View All tile is
       still an umbrella over a category name. Bring it up to date before
       the first paint, so the reading has something to write to. */
    if (section.getAttribute('data-vro-weather-filter') === '1') upgradeViewAll(section);

    /* The off state is the server's own markup, so it is already correct
       before any of this — but the copy and the switch still need setting,
       and they must not wait on a network request that may never answer. */
    paint(section);

    if (!isFinite(lat) || !isFinite(lon)) return;

    fetchWeather(lat, lon)
      .then(function (weather) {
        var described = describe(weather.code);
        var bucket = bucketFor(described, weather.temp, weather, thresholdsOf(section));
        /* The words follow the shelf, not the sky. A clear morning in a
           hill town whose low is 5° picks the winter shelf, and the copy
           has to say so: "sunny days" and "Sunny Wear" over a row of
           jackets read as a mistake. The sky's own words are kept only
           when the sky is what chose the shelf. */
        var words = bucket === described.bucket ? described : (BUCKET_WORDS[bucket] || described);
        section.__vroWeather = {
          bucket: bucket,
          values: {
            city: city,
            temp: weather.temp + '°',
            weather: words.word,
            wear: words.wear,
            conditions: words.phrase
          }
        };
        paint(section);
      })
      .catch(function () {
        /* Weather is unavailable but the city may still be worth showing,
           and only where the shopper asked for weather copy at all.
           Products are left exactly as rendered. */
        if (switchedOn(section)) applyText(section, { city: city });
      });
  }

  /* One handler for the page. Delegated, so a section that arrives later —
     a theme-editor re-render, or any AJAX that replaces a rail — is wired
     without re-running anything. */
  function watchSwitches() {
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-vro-weather-switch')) return;
      var section = input.closest('[data-vro-weather-toggle]');
      if (!section) return;
      rememberSwitch(section, input.checked);
      paint(section);
    });
  }

  /* The switch reflects the state this browser is in before a shopper
     touches it. */
  function syncSwitches() {
    document.querySelectorAll('[data-vro-weather-switch]').forEach(function (input) {
      var section = input.closest('[data-vro-weather-toggle]');
      if (section) input.checked = switchedOn(section);
    });
  }

  function sections() {
    return document.querySelectorAll('[data-vro-weather-city]');
  }

  function run() {
    sections().forEach(render);
  }

  /* A cached city only counts as "we know where they are" when it carries
     coordinates. The header writes this same key with a label and no
     coordinates when a logged-in shopper picks a saved address, and that
     must not pass for a detection — it is the reason a shopper whose
     header already read "Mumbai" was never asked. */
  function haveDetectedPosition() {
    var located = detectedLocation();
    return !!(located && typeof located.lat === 'number' && typeof located.lon === 'number');
  }

  function askedBefore() {
    try { return localStorage.getItem(ASKED_KEY) === '1'; } catch (e) { return false; }
  }
  function markAsked() {
    try { localStorage.setItem(ASKED_KEY, '1'); } catch (e) { /* private mode */ }
  }

  /* 'granted' | 'denied' | 'prompt', or null when the browser will not say.
     Only Chromium and Firefox answer for geolocation; Safari has
     navigator.permissions but not this query, and older versions throw
     rather than reject. Never throws and never rejects — null simply means
     "ask and find out", which is what every browser supports. */
  function permissionState(done) {
    var query = null;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        query = navigator.permissions.query({ name: 'geolocation' });
      }
    } catch (e) { query = null; }

    if (!query || typeof query.then !== 'function') { done(null); return; }

    query
      .then(function (status) { done(status && status.state); })
      .catch(function () { done(null); });
  }

  var cardOpen = false;

  function closeCard(card) {
    if (!card || !card.parentNode) return;
    cardOpen = false;
    card.classList.remove('is-open');
    setTimeout(function () {
      if (card.parentNode) card.parentNode.removeChild(card);
    }, 220);
  }

  /* Our own consent card. Shown only when the browser answered the page's
     request with the quiet address-bar chip rather than the dialog, so the
     shopper had nothing to press. Its button asks again from a real click,
     which always gets the dialog. */
  function showCard() {
    if (cardOpen || document.querySelector('.vro-loc-ask')) return;
    cardOpen = true;

    var card = document.createElement('div');
    card.className = 'vro-loc-ask';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', 'Use your location');
    card.innerHTML =
      '<button type="button" class="vro-loc-ask-close" aria-label="Close">&times;</button>'
      + '<div class="vro-loc-ask-icon" aria-hidden="true">'
      + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">'
      + '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>'
      + '</svg></div>'
      + '<div class="vro-loc-ask-body">'
      + '<p class="vro-loc-ask-title">Shop for your city</p>'
      + '<p class="vro-loc-ask-text">Allow location access and we’ll show you what works for today’s weather where you are.</p>'
      + '<div class="vro-loc-ask-actions">'
      + '<button type="button" class="vro-loc-ask-allow">Allow location</button>'
      + '<button type="button" class="vro-loc-ask-deny">Not now</button>'
      + '</div></div>';

    document.body.appendChild(card);
    /* Next frame, so the opening transition has a state to run from. */
    requestAnimationFrame(function () { card.classList.add('is-open'); });

    /* If the browser's own dialog was up after all and the shopper answers
       it, the card has nothing left to offer. */
    document.addEventListener('vro:location-changed', function onGot() {
      document.removeEventListener('vro:location-changed', onGot);
      closeCard(card);
    });

    card.querySelector('.vro-loc-ask-allow').addEventListener('click', function () {
      markAsked();
      closeCard(card);
      /* Straight from the click, so the browser treats it as a gesture — and
         not silent, because a shopper who just pressed a button should see
         it doing something. */
      if (window.VroLocation && window.VroLocation.detect) window.VroLocation.detect();
    });

    function decline() { markAsked(); closeCard(card); }
    card.querySelector('.vro-loc-ask-deny').addEventListener('click', decline);
    card.querySelector('.vro-loc-ask-close').addEventListener('click', decline);
  }

  /* Ask a first-time visitor, once. Never re-asks: a shopper who declined
     keeps the fallback city, and one who accepted already has a position
     cached. */
  function askOnce() {
    var wants = false;
    sections().forEach(function (s) {
      if (s.getAttribute('data-vro-weather-ask') === '1') wants = true;
    });
    /* The header's location pill is reason enough on its own, and it is on
       every page of the shop. Until we ask, the city it names is the
       merchant's fallback one — a shopper in Jaipur reading "Mumbai" was
       never asked anything, on a page that had no weather rail to do the
       asking. Still once per browser: the same flag guards both. */
    if (!wants && document.querySelector('.site-location')) wants = true;
    if (!wants) return;
    if (haveDetectedPosition()) return;
    /* A shopper who picked an address in the "deliver to" panel has already
       said where they are — the browser's dialog would be asking a question
       they have answered. */
    if (detectedLocation()) return;

    /* window.VroLocation is defined inside custom.js's own DOMContentLoaded
       handler, and custom.js is loaded at the end of the layout while this
       file is loaded from the section — both deferred, so this one runs
       first and VroLocation does not exist yet. Wait for it rather than
       checking once, which is what previously made the prompt never fire
       and left the heading on the fallback city. */
    waitFor(function () { return window.VroLocation && window.VroLocation.detect; }, function (ready) {
      if (!ready) return;
      permissionState(function (state) {
        /* Refused at the browser level: a page cannot undo that. */
        if (state === 'denied') return;
        /* Already granted: reading the position shows no dialog, so this is
           not an "ask" and the once-only flag does not apply. Silent, so a
           refresh never repaints the city the shopper already has. */
        if (state === 'granted') { window.VroLocation.detect({ silent: true }); return; }
        if (askedBefore()) return;

        markAsked();
        window.VroLocation.detect({ silent: true });
        watchForQuietPrompt();
      });
    });
  }

  /* Did the browser actually put the dialog up? Two ways to tell, because
     only some browsers offer the first:

     - The permission itself. Chromium and Firefox report it, and a request
       that is still 'prompt' seconds later was never shown to anyone.
     - Whether anything came back at all. custom.js announces both outcomes
       — a position, or a refusal/timeout — and getCurrentPosition is
       capped at ten seconds, so a dialog the shopper saw always produces
       one of them. Silence past that means nothing was ever put in front
       of them. This is what Safari runs on, where the permission query
       does not exist.

     Either way, an answered request is left alone: the card is for the
     shopper who was never asked, not for one who said no. */
  function watchForQuietPrompt() {
    var settled = false;
    var polls = 0;
    var poll, cap;

    function stop() {
      if (settled) return;
      settled = true;
      document.removeEventListener('vro:location-changed', onAnswered);
      document.removeEventListener('vro:location-failed', onAnswered);
      clearInterval(poll);
      clearTimeout(cap);
    }

    function onAnswered() { stop(); }

    document.addEventListener('vro:location-changed', onAnswered);
    document.addEventListener('vro:location-failed', onAnswered);

    poll = setInterval(function () {
      polls += 1;
      if (settled) { clearInterval(poll); return; }
      if (haveDetectedPosition()) { stop(); return; }
      permissionState(function (state) {
        if (settled) return;
        /* No answer from this browser: the cap below is the only signal. */
        if (state === null) return;
        if (state !== 'prompt') { stop(); return; }
        /* Still unanswered well past when a dialog would have appeared. */
        if (polls >= 12) { stop(); showCard(); }
      });
    }, 500);

    /* Longer than getCurrentPosition's own ten-second timeout, so a dialog
       the shopper is still reading has run its course before we conclude
       there was never one. */
    cap = setTimeout(function () {
      if (settled) return;
      stop();
      if (!haveDetectedPosition()) showCard();
    }, 12000);
  }

  /* Lets the header's "Use my current location" — or anything else — put the
     card back up after a shopper declined, without clearing storage. */
  window.VroWeather = window.VroWeather || {};
  window.VroWeather.askForLocation = function () {
    waitFor(function () { return window.VroLocation && window.VroLocation.detect; }, function (ready) {
      if (ready) showCard();
    });
  };

  /* ---------------------------------------------------------------
     The header's hover card.

     Where the shopper is, and what it is doing there. Hovering shows it;
     clicking is untouched and still opens the "deliver to" picker, so the
     card gets out of the way as soon as the picker opens.

     Only a position the shopper gave us is used. Without one, the card
     asks them to allow location instead: the fallback city's weather
     (Mumbai) printed there read as if it were theirs.
     --------------------------------------------------------------- */

  function fillHeaderCard(card, position, weather) {
    var described = describe(weather.code);

    function put(selector, text) {
      var el = card.querySelector(selector);
      if (el) el.textContent = text;
    }
    /* A reading the response did not carry prints an em dash rather than
       "NaN%" or a confident zero. */
    function reading(value, unit) {
      return typeof value === 'number' ? value + unit : '—';
    }

    put('[data-wx-city]', position.city || '');
    put('[data-wx-temp]', weather.temp + '°C');
    put('[data-wx-word]', described.word.charAt(0).toUpperCase() + described.word.slice(1));
    put('[data-wx-feels]', reading(weather.feels, '°C'));
    put('[data-wx-humidity]', reading(weather.humidity, '%'));
    put('[data-wx-rain]', reading(weather.rain, '%'));

    var cityEl = card.querySelector('[data-wx-city]');
    if (cityEl) cityEl.hidden = !position.city;
  }

  function setUpHeaderCard() {
    var card = document.querySelector('.site-location-weather');
    var trigger = document.querySelector('.site-location');
    if (!card || !trigger) return;

    var host = trigger.parentElement;
    var picker = host && host.querySelector('.site-location-picker');
    var ready = false;
    var pending = false;
    var wanted = false;

    function show() {
      /* The picker is the thing the shopper opened on purpose. */
      if (picker && picker.classList.contains('is-open')) return;
      if (ready) card.hidden = false;
    }
    function hide() { card.hidden = true; }

    /* No location shared: the card asks for it instead of printing the
       fallback city's weather as if it were the shopper's. */
    var ask = card.querySelector('[data-wx-ask]');
    function askMode(on) {
      card.classList.toggle('is-asking', on);
      if (ask) ask.hidden = !on;
      Array.prototype.forEach.call(card.children, function (el) {
        if (el !== ask) el.hidden = on;
      });
      if (!on) {
        var cityEl = card.querySelector('[data-wx-city]');
        if (cityEl) cityEl.hidden = !cityEl.textContent;
      }
    }
    var allow = card.querySelector('[data-wx-allow]');
    if (allow) {
      allow.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        allow.disabled = true;
        allow.textContent = 'Locating…';
        var started = window.VroLocation && window.VroLocation.detect ? window.VroLocation.detect() : false;
        if (!started && trigger) trigger.click();
        setTimeout(function () { allow.disabled = false; allow.textContent = 'Allow location'; }, 8000);
      });
    }

    function load() {
      if (ready || pending) { show(); return; }
      var located = detectedLocation();
      var position = (located && typeof located.lat === 'number' && typeof located.lon === 'number')
        ? { lat: located.lat, lon: located.lon, city: located.label } : null;
      if (!position) {
        askMode(true);
        ready = true;
        if (wanted) show();
        return;
      }
      pending = true;
      fetchWeather(position.lat, position.lon)
        .then(function (weather) {
          pending = false;
          fillHeaderCard(card, position, weather);
          askMode(false);
          ready = true;
          if (wanted) show();
        })
        .catch(function () { pending = false; });
    }

    /* Warm it on the first hover rather than on every page load: the card
       is worth one request, not one on every page nobody hovers. */
    var leaveTimer = null;
    function enter() {
      clearTimeout(leaveTimer);
      wanted = true;
      load();
      show();
    }
    /* A moment's grace, so crossing the gap between the trigger and the
       card does not close it under the pointer. */
    function leave() {
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(function () { wanted = false; hide(); }, 160);
    }

    host.addEventListener('mouseenter', enter);
    host.addEventListener('mouseleave', leave);
    card.addEventListener('mouseenter', enter);
    card.addEventListener('mouseleave', leave);
    /* Keyboard reaches it the same way. */
    trigger.addEventListener('focus', enter);
    trigger.addEventListener('blur', leave);
    /* A click hands over to the picker, which is what the shopper asked
       for — the card goes down and stays down until they hover again. */
    trigger.addEventListener('click', function () {
      clearTimeout(leaveTimer);
      wanted = false;
      hide();
    });

    /* A fresh detection changes both the city and the readings. The cache
       keys on coordinates, so re-reading is enough — no need to clear it. */
    document.addEventListener('vro:location-changed', function () {
      ready = false;
      if (allow) { allow.disabled = false; allow.textContent = 'Allow location'; }
      if (wanted || card.classList.contains('is-asking')) load();
    });
  }

  function start() {
    syncSwitches();
    watchSwitches();
    setUpHeaderCard();
    /* Before the early return below: the asking is now the header's as much
       as any rail's, and the header is on pages that carry no rail. */
    askOnce();
    if (!sections().length) return;
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Re-run when a location is detected or picked, so the rail follows the
     shopper without a reload. */
  document.addEventListener('vro:location-changed', run);
})();
